import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import type { CampaignTask } from '@bvengineering/cas-member-gateway-service-client';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  Link,
  List,
  LoadingIcon,
  Typography,
} from '@/primer/components';
import type { ListItemProps } from '@/primer/components';
import { PageColumn } from '@/components/layout';
import {
  ContentRequirements,
  TaskConfirmationModal,
  OptOutConfirmationModal,
  ReservationTimerBanner,
  ReservationSlowDownModal,
} from '@/components/ui';
import { useCampaign } from '@/api';
import { useIsProviderConnected } from '@/api/queries/settings/useQuerySocialConnections';
import { SocialProvider } from '@/api/queries/settings/constants';
import { useReservationExpiry } from '@/hooks/useReservationTimer';
import { getTaskDisplay, isSupportedTaskType } from '@/utils/taskTypes';
import { MS_PER_DAY, findReservedUserProduct } from '@/utils';
import {
  normalizeTaskState,
  TASK_STATE,
  TASK_VALIDATING_MESSAGE,
  TASK_DISABLED_MESSAGE,
} from '@/utils/taskState';
import { parseTaskRejectionReason } from '@/utils/taskRejectionReasons';

const SUPPORT_EMAIL = 'creators@influenster.com';

/** Builds the state-driven list row for a deliverable task. */
function deliverableRowItem(task: CampaignTask, openTask: () => void): ListItemProps {
  const display = getTaskDisplay(task.type);
  const canOpen = isSupportedTaskType(task.type);
  const base = { icon: display.icon, title: display.name };

  switch (normalizeTaskState(task.state)) {
    case TASK_STATE.COMPLETED:
      return { ...base, trailingIcon: 'circle-tick-filled', trailingIconColor: 'success' };
    case TASK_STATE.VALIDATING:
      return { ...base, subtitle: TASK_VALIDATING_MESSAGE, loading: true };
    case TASK_STATE.REJECTED: {
      const rejectionMessage = parseTaskRejectionReason(task.stateReason, task.type).message;
      if (!canOpen) {
        return {
          ...base,
          subtitle: rejectionMessage,
          subtitleColor: 'error',
          disabled: true,
        };
      }
      return {
        ...base,
        subtitle: rejectionMessage,
        subtitleColor: 'error',
        trailingIcon: 'chevron-right',
        trailingIconColor: 'light',
        onClick: openTask,
      };
    }
    case TASK_STATE.PERFORMABLE:
      if (canOpen) {
        return {
          ...base,
          trailingIcon: 'chevron-right',
          trailingIconColor: 'light',
          onClick: openTask,
        };
      }
      return {
        ...base,
        subtitle: TASK_DISABLED_MESSAGE,
        trailingIcon: 'chevron-right',
        disabled: true,
      };
    // Locked until the product ships.
    case TASK_STATE.DISABLED:
    default:
      return {
        ...base,
        subtitle: TASK_DISABLED_MESSAGE,
        trailingIcon: 'chevron-right',
        disabled: true,
      };
  }
}

/**
 * Review hub shown after Select Product: the creator reviews the required tasks
 * and content requirements before continuing.
 */
export function CampaignTasks() {
  const { id: campaignId } = useParams({ from: '/_authenticated/campaign/$id/tasks' });
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const {
    isConnected: isInstagramConnected,
    username: instagramUsername,
    isLoading: isConnectionLoading,
    isError: isConnectionError,
  } = useIsProviderConnected(SocialProvider.INSTAGRAM);
  const reservedUserProduct = findReservedUserProduct(campaign?.userProducts);
  const reservationExpired = useReservationExpiry(reservedUserProduct?.reservedDate ?? '');

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showContentSheet, setShowContentSheet] = useState(false);
  const [tasksReviewed, setTasksReviewed] = useState(false);
  const [contentReviewed, setContentReviewed] = useState(false);
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingIcon size="xl" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <PageColumn className="text-center py-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="header" size="h1" color="error" as="h1">
            Campaign Not Found
          </Typography>
          <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Dashboard</Button>
        </div>
      </PageColumn>
    );
  }

  const tasks = campaign.tasks ?? [];
  const products = campaign.products ?? [];

  const totalTaskCount = campaign.totalTaskCount;
  const completedTaskCount = campaign.completedTaskCount;

  const daysLeft = campaign.expired
    ? Math.max(0, Math.ceil((new Date(campaign.expired).getTime() - Date.now()) / MS_PER_DAY))
    : null;

  // Opt-out is disabled once the product is claimed (existing backend guard).
  const hasClaimed = (campaign.userProducts ?? []).some(
    (product) => !!product.claimedDate || product.state?.toLowerCase() === 'claimed'
  );

  const hasTasks = tasks.length > 0;
  const canContinue = (!hasTasks || tasksReviewed) && contentReviewed;

  const reviewItems: ListItemProps[] = [
    ...(hasTasks
      ? [
          {
            icon: 'tasks',
            title: 'Required tasks',
            subtitle: "View what's needed to get your free product(s).",
            trailingIcon: tasksReviewed ? 'circle-tick-filled' : 'chevron-right',
            trailingIconColor: tasksReviewed ? 'success' : 'light',
            onClick: () => setShowTaskModal(true),
          } satisfies ListItemProps,
        ]
      : []),
    {
      icon: 'image-tick',
      title: 'Content requirements',
      subtitle: 'Tips, guides and inspiration for creating your best content.',
      trailingIcon: contentReviewed ? 'circle-tick-filled' : 'chevron-right',
      trailingIconColor: contentReviewed ? 'success' : 'light',
      onClick: () => setShowContentSheet(true),
    },
    ...tasks.map(
      (task): ListItemProps =>
        deliverableRowItem(task, () =>
          navigate({
            to: '/campaign/$id/tasks/$taskId',
            params: { id: campaignId, taskId: String(task.id) },
          })
        )
    ),
  ];

  const handleTasksReviewed = () => {
    setTasksReviewed(true);
    setShowTaskModal(false);
  };

  const handleContentReviewed = () => {
    setContentReviewed(true);
    setShowContentSheet(false);
  };

  const handleNext = () => {
    // Wait for the connection status to resolve before gating — the hook reports
    // isConnected=false while /settings/social is still loading, which would
    // otherwise misroute an already-connected creator to the connect screen.
    // Treat a fetch error the same way: don't gate on isConnected=false when the
    // query failed, as the creator may already be connected.
    if (isConnectionLoading || isConnectionError) return;
    // Instagram must be connected before continuing; gate to the connect screen.
    if (!isInstagramConnected) {
      navigate({ to: '/campaign/$id/connect', params: { id: campaignId } });
      return;
    }
    // The Confirm Address / Claim step is delivered separately; route to the
    // dashboard for now so the reviewed flow has a terminal destination.
    navigate({ to: '/dashboard' });
  };

  return (
    <PageColumn className="py-4 flex flex-col gap-6">
      {reservedUserProduct?.reservedDate && (
        <>
          <ReservationTimerBanner
            campaignId={campaignId}
            reservedDate={reservedUserProduct.reservedDate}
          />
          {!reservationExpired && <ReservationSlowDownModal />}
        </>
      )}
      <div className="flex items-center gap-3">
        <IconButton
          icon="chevron-left"
          onClick={() =>
            navigate({
              to: '/campaign/$id/select-product',
              params: { id: campaignId },
              replace: true,
            })
          }
          aria-label="Go back"
        />
        <Typography variant="header" size="h1" as="h1">
          {campaign.name ?? 'Your campaign'}
        </Typography>
      </div>

      <div className="flex items-center gap-3 rounded-medium bg-interactive-quiet p-3">
        {products.length > 0 && (
          <div className="flex -space-x-2 flex-shrink-0" aria-hidden="true">
            {products.slice(0, 3).map((product) => (
              <Avatar
                key={product.casProductId}
                src={product.productImageUrl}
                fallbackIcon="bag"
                size="medium"
              />
            ))}
          </div>
        )}
        <Typography variant="body" size="small" color="interactive" as="span">
          {daysLeft != null
            ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left to complete your tasks`
            : 'Complete your tasks to get your free product.'}
        </Typography>
        <span className="ml-auto shrink-0 rounded-rounded bg-interactive-loud px-2 py-0.5">
          <Typography variant="body" size="xs" color="white" as="span">
            {completedTaskCount}/{totalTaskCount}
          </Typography>
        </span>
      </div>

      <div className="flex flex-col gap-4">
        <Typography variant="header" size="h2" as="h2">
          Please review the following:
        </Typography>

        <List shadow="first" items={reviewItems} />
      </div>

      <div className="flex flex-col gap-2">
        <Typography variant="header" size="h2" as="h2">
          Questions about this collab?
        </Typography>
        <Typography variant="body" size="medium" color="bodylight">
          If something doesn't look right or you have questions about the requirements, reach out to
          our team at <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </div>

      <Divider />

      <div className="flex gap-3">
        <Button
          variant="secondary"
          size="large"
          fullWidth
          onClick={() => setShowOptOutConfirm(true)}
          disabled={hasClaimed}
        >
          Opt out
        </Button>
        <Button
          variant="primary"
          size="large"
          fullWidth
          onClick={handleNext}
          disabled={!canContinue || isConnectionLoading || isConnectionError}
        >
          Next
        </Button>
      </div>

      <TaskConfirmationModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onConfirm={handleTasksReviewed}
        tasks={tasks}
        handle={instagramUsername}
      />

      <ContentRequirements
        campaignId={campaignId}
        isOpen={showContentSheet}
        onClose={() => setShowContentSheet(false)}
        onAcknowledge={handleContentReviewed}
      />

      <OptOutConfirmationModal
        isOpen={showOptOutConfirm}
        onClose={() => setShowOptOutConfirm(false)}
        campaignId={campaignId}
      />
    </PageColumn>
  );
}

export default CampaignTasks;
